import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import OnboardingChecklist from "../components/OnboardingChecklist";
import PrimaryGlDialog from "../components/PrimaryGlDialog";
import NeedsAttentionTile from "../components/NeedsAttentionTile";
import CompanyGroups from "../components/CompanyGroups";
import NoticeBanners from "../components/NoticeBanners";
import StatusPill from "../components/StatusPill";
import PaymentPill from "../components/PaymentPill";
import { formatCurrency, formatDateTime, timeAgo, erpLabel } from "../lib/format";
import { RefreshCw, Plug, AlertTriangle, CheckCircle2, Clock,
         ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div className="card-flat p-5 flex flex-col gap-1" data-testid={testid}>
      <div className="label-mini">{label}</div>
      <div className={`font-display text-4xl font-bold tracking-tight ${accent || ""}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

function InvoiceMiniTable({ title, icon: Icon, direction, rows, emptyMsg,
                            onOpen, onViewAll, testid }) {
  // direction: 'receiving' shows Vendor; 'sending' shows Customer.
  const counterpartyHeader = direction === "receiving" ? "vendor" : "customer";
  return (
    <div className="card-flat" data-testid={testid}>
      <div className="px-5 py-4 border-b border-neutral-900 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon size={16} />
          <div>
            <div className="label-mini">
              {direction === "receiving" ? "inbound" : "outbound"}
            </div>
            <h3 className="font-display text-xl font-semibold">{title}</h3>
          </div>
        </div>
        <button
          type="button"
          className="btn-outline"
          onClick={onViewAll}
          data-testid={`${testid}-view-all`}
        >
          view all
        </button>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>invoice #</th>
            <th>{counterpartyHeader}</th>
            <th className="text-right">amount</th>
            <th>payment</th>
            <th>status</th>
            <th>updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={6} className="text-center text-neutral-500 py-10">{emptyMsg}</td></tr>
          )}
          {rows.map((inv) => (
            <tr key={inv.id} onClick={() => onOpen(inv)}
                data-testid={`${testid}-row-${inv.invoice_number}`}>
              <td className="font-mono">{inv.invoice_number}</td>
              <td>
                {direction === "receiving" ? (
                  <div className="text-neutral-900">{inv.sender_company_name || "—"}</div>
                ) : (
                  <>
                    <div className="text-neutral-900">{inv.customer_name}</div>
                    <div className="text-xs text-neutral-500">{inv.customer_email}</div>
                  </>
                )}
              </td>
              <td className="text-right font-mono">{formatCurrency(inv.total_amount, inv.currency)}</td>
              <td><PaymentPill invoice={inv} /></td>
              <td><StatusPill invoice={inv} /></td>
              <td className="text-xs text-neutral-500 font-mono">{timeAgo(inv.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderConnectionTile(c) {
  return (
    <div key={c.id} className="card-flat p-5 flex items-center justify-between"
         data-testid={`connection-card-${c.id}`}>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 border border-neutral-900 flex items-center justify-center">
          <Plug size={16} />
        </div>
        <div>
          <div className="font-display font-semibold text-base leading-tight">{c.name}</div>
          <div className="text-xs text-neutral-500 mt-0.5">
            account · <span className="text-neutral-900">{c.account_id}</span>
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="flex items-center justify-end gap-2 text-xs">
          <span className="status-dot"
                style={{ background: c.connected ? "var(--success)" : "var(--error)" }} />
          <span className="uppercase tracking-widest">{c.connected ? "connected" : "offline"}</span>
        </div>
        <div className="label-mini mt-2">last sync</div>
        <div className="text-xs font-mono">{timeAgo(c.last_sync_at)}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [connections, setConnections] = useState([]);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    // Each call settles independently so one slow/failed call never blanks the Dashboard.
    const [d, ev, conn] = await Promise.allSettled([
      api.get("/dashboard"),
      api.get("/sync/events", { params: { limit: 8 } }),
      api.get("/connections"),
    ]);
    if (d.status === "fulfilled") setData(d.value.data);
    if (ev.status === "fulfilled") setEvents(ev.value.data);
    if (conn.status === "fulfilled") setConnections(conn.value.data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const runSync = async () => {
    setRunning(true);
    try {
      await api.post("/sync/run");
      await load();
    } finally {
      setRunning(false);
    }
  };

  const stats = data?.stats || {};
  const mapping = data?.mapping_summary;

  return (
    <Layout>
      <PageHeader
        eyebrow="control room"
        title="Sync Overview"
        description="Your company's activity — invoices your vendors sent you (receiving) and invoices you sent your customers (sending). Scheduler runs every 5 minutes."
        testid="dashboard-header"
        actions={
          <button
            type="button"
            className="btn-primary flex items-center gap-2"
            onClick={runSync}
            disabled={running}
            data-testid="run-sync-button"
          >
            <RefreshCw size={14} className={running ? "pulse" : ""} />
            {running ? "running…" : "run sync now"}
          </button>
        }
      />

      <div className="p-8 space-y-8">
        <OnboardingChecklist />
        <PrimaryGlDialog />
        <NoticeBanners testid="dashboard-notice-banners" />
        <NeedsAttentionTile />
        {mapping && mapping.unmatched > 0 && (
          <div className="border border-red-600 bg-red-50 px-4 py-3 flex items-start justify-between gap-4"
               data-testid="needs-mapping-banner">
            <div className="flex items-start gap-3 text-sm">
              <AlertTriangle size={16} className="text-red-600 mt-0.5" />
              <div>
                <strong>{mapping.unmatched}</strong> item{mapping.unmatched === 1 ? "" : "s"} need mapping to a GL account.
                {mapping.needs_mapping_invoices > 0 && (
                  <span> <strong>{mapping.needs_mapping_invoices}</strong> invoice{mapping.needs_mapping_invoices === 1 ? "" : "s"} held pending your decision.</span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="text-xs uppercase tracking-widest underline"
              onClick={() => navigate("/mappings?status=unmatched")}
              data-testid="needs-mapping-cta"
            >
              open mappings →
            </button>
          </div>
        )}

        {/* Stats — split into Receiving / Sending groups */}
        {data?.scope === "all" && (
          <div className="label-mini -mb-4" data-testid="stats-scope-all">All companies</div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard label="receiving · total" value={stats.receiving_total ?? "—"}
                    sub="invoices in from vendors" testid="stat-receiving-total" />
          <StatCard label="receiving · synced" value={stats.receiving_synced ?? "—"}
                    accent="text-green-600" sub="posted to your GL"
                    testid="stat-receiving-synced" />
          <StatCard label="receiving · held" value={stats.receiving_held ?? "—"}
                    accent="text-amber-600" sub="needs mapping / approval / match"
                    testid="stat-receiving-held" />
          <StatCard label="sending · total" value={stats.sending_total ?? "—"}
                    sub="invoices out to customers" testid="stat-sending-total" />
          <StatCard label="sending · synced" value={stats.sending_synced ?? "—"}
                    accent="text-green-600" sub="landed in customer's GL"
                    testid="stat-sending-synced" />
          <StatCard label="sending · held" value={stats.sending_held ?? "—"}
                    accent="text-amber-600" sub="unmatched / needs action"
                    testid="stat-sending-held" />
        </div>

        {/* Connections row */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mini">connections</div>
              <h2 className="font-display text-2xl tracking-tight font-semibold">ERP Endpoints</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="connections-strip">
            {data?.scope !== "all" && connections.map(renderConnectionTile)}
          </div>
          {data?.scope === "all" && (
            <CompanyGroups connections={connections} renderItem={renderConnectionTile}
                           testid="admin-connection-groups" limit={5}
                           moreLink={<button type="button" className="underline" onClick={() => navigate("/connections")}
                                             data-testid="admin-connection-groups-view-all">view all on Connections</button>} />
          )}
        </div>

        {/* Two invoice tables side-by-side */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <InvoiceMiniTable
            title="Receiving"
            icon={ArrowDownToLine}
            direction="receiving"
            rows={data?.receiving || []}
            emptyMsg="No invoices in from vendors yet."
            onOpen={(inv) => navigate(`/invoices/${inv.id}`)}
            onViewAll={() => navigate("/invoices")}
            testid="receiving-table"
          />
          <InvoiceMiniTable
            title="Sending"
            icon={ArrowUpFromLine}
            direction="sending"
            rows={data?.sending || []}
            emptyMsg="No invoices out to customers yet."
            onOpen={(inv) => navigate(`/invoices/${inv.id}`)}
            onViewAll={() => navigate("/invoices")}
            testid="sending-table"
          />
        </div>

        {/* Activity */}
        <div className="card-flat">
          <div className="px-5 py-4 border-b border-neutral-900">
            <div className="label-mini">live feed</div>
            <h3 className="font-display text-xl font-semibold">Activity</h3>
          </div>
          <div className="divide-y divide-neutral-200" data-testid="activity-feed">
            {events.length === 0 && (
              <div className="text-xs text-neutral-500 px-5 py-6">no activity yet — kick a sync above.</div>
            )}
            {events.map((ev) => (
              <div key={ev.id} className="px-5 py-4 flex gap-3" data-testid={`activity-event-${ev.id}`}>
                <div className="mt-0.5">
                  {ev.status === "synced" && <CheckCircle2 size={14} className="text-green-600" />}
                  {ev.status === "failed" && <AlertTriangle size={14} className="text-red-600" />}
                  {ev.status === "pending" && <Clock size={14} className="text-amber-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs">
                    <span className="font-mono">{ev.invoice_number}</span>{" "}
                    <span className="text-neutral-500">
                      {erpLabel(ev.source_erp)} → {erpLabel(ev.target_erp)}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-700 mt-1 break-words">{ev.message}</div>
                  <div className="label-mini mt-1">{formatDateTime(ev.created_at)} · {ev.triggered_by}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
